import axios from 'axios'
import APIService from '@/api/services/APIService/APIServiceImpl'
import ContactServiceV1 from '@/api/services/ContactService/ContactServiceV1'

import { ContactEditDTO, ContactEditResponseDTO, ContactLoginDTO, ContactLoginResponseDTO, ContactNewPasswordDTO, ContactNewPasswordResponseDTO, ContactRefreshResponseDTO, ContactRegisterDTO, ContactRegisterResponseDTO, ContactResetPasswordDTO, ContactResetPasswordResponseDTO, ContactSearchDTO, ContactSearchResponseDTO, SubscribeNewsletterDTO, UserInfo, getWebsiteMapping } from "../../../../../requests-module"
import getCookie from '@/utils/getCookie'
import { isCSE } from '@/utils/cse'
import { Locale } from '@/service/translate/flights/flightTranslations.types'



export default class ContactServiceV1Impl
    extends APIService
    implements ContactServiceV1 {
    constructor() {
        const serviceUrl = `https://api.travelski.com/account/BookingFunnelContact`
        // const serviceUrl = `https://api.travelski.com/account/BookingFunnelContact`
        super(serviceUrl)
    }

    async login(dto: ContactLoginDTO): Promise<ContactLoginResponseDTO | null> {
        try {
            const params = {
                ...this.getDefaultParams()
            }
            const headers = {
                ...this.getDefaultHeaders(),
                locale: window.locale.replace("_", '-'),
            }
            const url = `${this.getServiceUrl()}/contact/login`
            const { data } = await axios.post(url, JSON.stringify(dto), {
                params,
                headers,
            })
            return data
        } catch (err) {
            console.error(err)
            return null
        }
    }

    async register(dto: ContactRegisterDTO): Promise<ContactRegisterResponseDTO | null> {
        if (dto.country.length > 2) throw new Error(`Field "country" must be no more than 2 characters long`)
        try {
            const params = {
                ...this.getDefaultParams()
            }
            const headers = {
                ...this.getDefaultHeaders(),
                locale: window.locale.replace("_", '-'),
            }
            const url = `${this.getServiceUrl()}/contact`
            const { data } = await axios.post(url, JSON.stringify(dto), {
                params,
                headers,
            })
            return data
        } catch (err: any) {
            // If this is an AxiosError with a JSON body that has a `message` field,
            // extract that and throw a new Error so downstream just sees `err.message`.
            if (axios.isAxiosError(err) && err.response?.data) {
                const apiMessage = (err.response.data as any).message
                console.error('ContactService.edit ▶️ API error payload:', err.response.data)
                throw new Error(apiMessage || err.message)
            }
            // fallback
            console.error('ContactService.edit ▶️ unexpected error:', err)
            throw err
        }
    }

    async search(dto: ContactSearchDTO): Promise<ContactSearchResponseDTO | null> {
        try {
            const params = {
                email: dto.email
            }
            const headers = {
                ...this.getDefaultHeaders(),
                locale: window.locale.replace("_", '-'),
            }
            const url = `${this.getServiceUrl()}/contact`
            const { data } = await axios.get(url, {
                params,
                headers,
            })
            return data
        } catch (err) {
            console.error(err)
            return null
        }
    }

    /**
     * Load full CRM profile by email (orchestrator getUserInfo parity).
     * Falls back to null if search POST is unavailable from the browser.
     */
    async fetchUserInfoByEmail(email: string, website: string, company?: string): Promise<UserInfo | null> {
        if (!email?.trim()) {
            return null
        }
        try {
            const siteLocale = getWebsiteMapping(website).contactLocale + (company ? `-${company}` : '')
            const url = `${this.getServiceUrl()}/contact/search`
            const searchOptions = {
                filter: { email },
                page: 0,
                resultsPerPage: 10,
                site_locale: siteLocale,
                sort: {},
            }
            const headers = {
                ...this.getDefaultHeaders(),
                locale: window.locale.replace('_', '-'),
            }
            const { data } = await axios.post(url, searchOptions, { headers })
            const matchedUser = data?.users?.find((user: UserInfo) => user.email === email)
            return matchedUser ?? null
        } catch (err) {
            console.error('[ContactService.fetchUserInfoByEmail]', err)
            return null
        }
    }


    async edit(dto: ContactEditDTO): Promise<ContactEditResponseDTO | null> {
        try {
            const params = {
                ...this.getDefaultParams()
            }
            const headers = {
                ...this.getDefaultHeaders(),
                Authorization: getCookie('user-id-token') || "",
                locale: window.locale.replace("_", '-'),
            }
            const url = `${this.getServiceUrl()}/contact`
            const { data } = await axios.put(url, JSON.stringify(dto), {
                params,
                headers,
            })
            return data
        } catch (err: any) {
            // If this is an AxiosError with a JSON body that has a `message` field,
            // extract that and throw a new Error so downstream just sees `err.message`.
            if (axios.isAxiosError(err) && err.response?.data) {
                const apiMessage = (err.response.data as any).message
                console.error('ContactService.edit ▶️ API error payload:', err.response.data)
                throw new Error(apiMessage || err.message)
            }
            // fallback
            console.error('ContactService.edit ▶️ unexpected error:', err)
            throw err
        }
    }

    async resetPassword(dto: ContactResetPasswordDTO): Promise<ContactResetPasswordResponseDTO | null> {
        try {
            const params = {
                ...this.getDefaultParams()
            }
            const headers = {
                ...this.getDefaultHeaders(),
                // Authorization: getCookie('user-id-token') || "",
                locale: window.locale.replace("_", '-'),
            }
            const url = `${this.getServiceUrl()}/contact/forgot-password`
            const { data } = await axios.post(url, JSON.stringify(dto), {
                params,
                headers,
            })
            return data
        } catch (err) {
            console.error(err)
            return null
        }
    }

    async newPassword(dto: ContactNewPasswordDTO): Promise<ContactNewPasswordResponseDTO | null> {
        try {
            const params = {
                ...this.getDefaultParams()
            }
            const headers = {
                ...this.getDefaultHeaders(),
                // Authorization: getCookie('user-id-token') || "",
                locale: window.locale.replace("_", '-'),
            }
            const url = `${this.getServiceUrl()}/contact/forgot-password/reset`
            const { data } = await axios.post(url, JSON.stringify(dto), {
                params,
                headers,
            })
            return data
        } catch (err) {
            console.error(err)
            throw err
        }
    }

    async refresh(): Promise<ContactRefreshResponseDTO | null> {
        const params = {
            ...this.getDefaultParams()
        }
        const headers = {
            ...this.getDefaultHeaders(),
            Authorization: getCookie('user-refresh-token') || "",
            locale: window.locale.replace("_", '-'),
        }
        const url = `${this.getServiceUrl()}/contact/refresh`
        try {
            const ContactRefreshResponseDTO: ContactRefreshResponseDTO = await axios.get(url, {
                params,
                headers,
            })
            return ContactRefreshResponseDTO
        } catch (err) {
            console.error(err)
            return null
        }
    }

    async subscribeNewsletter(dto: SubscribeNewsletterDTO): Promise<SubscribeNewsletterDTO | null> {
        try {
            const params = {
                ...this.getDefaultParams()
            }
            const headers = {
                ...this.getDefaultHeaders(),
                Authorization: getCookie('user-id-token') || "",
                locale: window.locale.replace("_", '-'),
            }
            const url = `${this.getServiceUrl()}/contact/subscribe-newsletter`
            const { data } = await axios.post(url, { ...dto }, {
                params,
                headers,
            })
            return data
        } catch (err) {
            console.error(err)
            return null
        }
    }




    public getDefaultParams(): {
        [key: string]: string;
    } {
        const params: { [key: string]: string } = {}
        if (window.company && isCSE(window.locale as Locale)) params['company'] = window.company
        return params
    }

}